/**
 * Config tab — Manager+ surface for Bookings business setup.
 *
 * One row per branch. Manager pastes the Bookings business id (or the
 * "Copy public link" URL) from MS Bookings; the value is persisted to
 * local storage today and migrates to the v3 SQL `Branches.bookingsBusinessId`
 * column when the admin Config DB schema lands.
 *
 * This is the ONLY place Bookings business setup lives — there is no
 * duplicate Config screen elsewhere in the app per Bill's "no duplication
 * or confusion" mandate.
 */

import { useEffect, useMemo, useState } from 'react';
import { Button, Pill, Banner, DataGrid, type DataGridColumn } from '@/components/primitives';
import { useConfig } from '@/contexts/ConfigContext';
import { useAuth } from '@/contexts/AuthContext';
import {
  readLocalBookingsConfig,
  writeLocalBookingsConfig,
  listBookingsBusinesses,
} from '../services/graphBookingsClient';
import type { BookingsBusinessConfig } from '../types';

interface Row {
  branchCode: string;
  branchName: string;
  bookingsBusinessId: string;
  publicBookingUrl: string;
}

function liveEnabled(): boolean {
  const env = (import.meta as unknown as { env: Record<string, string | undefined> }).env;
  return env['VITE_FEATURE_APPOINTMENTS_LIVE'] === 'true';
}

export function ConfigTab(): JSX.Element {
  const { config } = useConfig();
  const { token } = useAuth();
  const [stored, setStored] = useState<ReadonlyArray<BookingsBusinessConfig>>(() =>
    readLocalBookingsConfig(),
  );
  const [businesses, setBusinesses] = useState<ReadonlyArray<{ id: string; displayName: string; publicUrl?: string }>>([]);
  const [discoverErr, setDiscoverErr] = useState<string | null>(null);

  // Discover the Bookings businesses the signed-in user can manage.
  useEffect(() => {
    if (!liveEnabled() || !token) return;
    let cancelled = false;
    void (async () => {
      try {
        const list = await listBookingsBusinesses(token);
        if (!cancelled) setBusinesses(list);
      } catch (e) {
        if (!cancelled) setDiscoverErr(e instanceof Error ? e.message : String(e));
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  // Hydrate one row per branch.
  const rows = useMemo<Row[]>(() => {
    return config.branches
      .filter((b) => b.isActive)
      .map((b) => {
        const cfg = stored.find((s) => s.branchCode === b.branchCode);
        return {
          branchCode: b.branchCode,
          branchName: b.branchName,
          bookingsBusinessId: cfg?.bookingsBusinessId ?? '',
          publicBookingUrl: cfg?.publicBookingUrl ?? '',
        };
      });
  }, [config.branches, stored]);

  function updateRow(branchCode: string, patch: Partial<BookingsBusinessConfig>): void {
    const merged = (() => {
      const existing = stored.find((r) => r.branchCode === branchCode);
      if (!existing) {
        return [...stored, { branchCode, bookingsBusinessId: '', ...patch } as BookingsBusinessConfig];
      }
      return stored.map((r) => (r.branchCode === branchCode ? { ...r, ...patch } : r));
    })();
    writeLocalBookingsConfig(merged);
    setStored(merged);
  }

  const columns: DataGridColumn<Row>[] = [
    { key: 'branch', label: 'Branch', render: (r) => `${r.branchName} (${r.branchCode})` },
    {
      key: 'bookingsBusinessId',
      label: 'Bookings business',
      render: (r) => (
        businesses.length > 0 ? (
          <select
            value={r.bookingsBusinessId}
            onChange={(e) => {
              const id = e.target.value;
              const biz = businesses.find((b) => b.id === id);
              const patch: Partial<BookingsBusinessConfig> = { bookingsBusinessId: id };
              if (biz?.displayName) patch.displayName = biz.displayName;
              if (biz?.publicUrl) patch.publicBookingUrl = biz.publicUrl;
              updateRow(r.branchCode, patch);
            }}
            className="appt-config__select"
          >
            <option value="">— Not configured —</option>
            {businesses.map((b) => (
              <option key={b.id} value={b.id}>{b.displayName || b.id}</option>
            ))}
          </select>
        ) : (
          <input
            type="text"
            value={r.bookingsBusinessId}
            placeholder="paste Bookings business id"
            onChange={(e) => updateRow(r.branchCode, { bookingsBusinessId: e.target.value })}
            className="appt-config__input"
          />
        )
      ),
    },
    {
      key: 'publicBookingUrl',
      label: 'Public link',
      render: (r) => (
        <input
          type="url"
          value={r.publicBookingUrl}
          placeholder="https://outlook.office.com/bookwithme/…"
          onChange={(e) => updateRow(r.branchCode, { publicBookingUrl: e.target.value })}
          className="appt-config__input"
        />
      ),
    },
    {
      key: 'status',
      label: 'Status',
      render: (r) =>
        r.bookingsBusinessId
          ? <Pill variant="ok">Configured</Pill>
          : <Pill variant="muted">Not configured</Pill>,
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (r) => (
        <Button
          size="sm"
          variant="ghost"
          onClick={() => updateRow(r.branchCode, { bookingsBusinessId: '', publicBookingUrl: '' })}
          disabled={!r.bookingsBusinessId && !r.publicBookingUrl}
        >
          Clear
        </Button>
      ),
    },
  ];

  return (
    <section className="appt-tab" aria-label="Appointments configuration">
      <div className="appt-tab__bar">
        <div>
          <h2>Bookings business setup</h2>
          <span className="appt-tab__bar-sub">
            Map each PCB branch to its Microsoft Bookings business. Customers self-book via the public link.
          </span>
        </div>
      </div>

      {!liveEnabled() && (
        <Banner tone="info">
          Live discovery of Bookings businesses is gated behind <code>VITE_FEATURE_APPOINTMENTS_LIVE</code>.
          Until enabled, paste the Bookings business id manually — values are persisted locally and
          migrate to <code>Branches.bookingsBusinessId</code> at ROP cutover.
        </Banner>
      )}
      {discoverErr && (
        <Banner tone="warn">
          Could not list Bookings businesses for the signed-in user: {discoverErr}.
          Check the <code>Bookings.Read.All</code> scope.
        </Banner>
      )}

      <DataGrid<Row> columns={columns} rows={rows} rowKey={(r) => r.branchCode} />

      <p className="appt-config__foot">
        Default appointment templates and per-branch availability live in the{' '}
        <strong>Admin / Lookups / NotificationTemplates</strong> screen — not here.
      </p>
    </section>
  );
}
